/**
 * paths.js — every Firestore path in the application is built here.
 *
 * WHY THIS EXISTS
 * v1 built paths inline in ~30 components with template strings. One of them
 * (`party-details/index.js:52`) built `doc(db, "Parties", id)` — a root-level
 * collection with no tenant segment — so "delete party" silently did nothing,
 * and would have deleted across tenants had that collection existed.
 *
 * Centralising the paths means the tenant segment cannot be forgotten: every
 * builder takes `businessId` as its first argument and throws without it.
 */

import { collection, doc } from 'firebase/firestore';
import { db } from '../firebase';

function requireBusiness(businessId) {
  if (!businessId || typeof businessId !== 'string') {
    throw new Error('A businessId is required to build a Firestore path.');
  }
  return businessId;
}

/* Root, non-tenant collections ------------------------------------- */

export const usersCol = () => collection(db, 'Users');
export const userDoc = (uid) => doc(db, 'Users', uid);
export const businessesCol = () => collection(db, 'Businesses');
export const businessDoc = (businessId) => doc(db, 'Businesses', requireBusiness(businessId));

/* Tenant-scoped ----------------------------------------------------- */

const sub = (businessId, ...segments) =>
  collection(db, 'Businesses', requireBusiness(businessId), ...segments);

const subDoc = (businessId, ...segments) =>
  doc(db, 'Businesses', requireBusiness(businessId), ...segments);

export const settingsCol = (b) => sub(b, 'Settings');
export const settingsDoc = (b, key) => subDoc(b, 'Settings', key);

export const countersCol = (b) => sub(b, 'Counters');
export const counterDoc = (b, key) => subDoc(b, 'Counters', key);

export const partiesCol = (b) => sub(b, 'Parties');
export const partyDoc = (b, id) => subDoc(b, 'Parties', id);

export const itemsCol = (b) => sub(b, 'Items');
export const itemDoc = (b, id) => subDoc(b, 'Items', id);

export const categoriesCol = (b) => sub(b, 'ItemCategories');
export const categoryDoc = (b, id) => subDoc(b, 'ItemCategories', id);

export const batchesCol = (b, itemId) => sub(b, 'Items', itemId, 'Batches');
export const batchDoc = (b, itemId, id) => subDoc(b, 'Items', itemId, 'Batches', id);

/**
 * Serial numbers, one document per physical unit.
 *
 * A subcollection of the item rather than a field on it: a serialised item can
 * have thousands of units, and a Firestore document is capped at 1 MiB. More
 * importantly, a serial has its own lifecycle — in stock, sold, returned,
 * scrapped — and a document is the only shape that can carry it.
 */
export const serialsCol = (b, itemId) => sub(b, 'Items', itemId, 'Serials');
export const serialDoc = (b, itemId, id) => subDoc(b, 'Items', itemId, 'Serials', id);

/**
 * Godowns, branches, vans — anywhere stock physically sits.
 *
 * Stock is counted per location AND in total: the item carries both
 * `stockOnHand` and a `stockByWarehouse` map, so a list screen needs one read
 * and a location screen needs no join.
 */
export const warehousesCol = (b) => sub(b, 'Warehouses');
export const warehouseDoc = (b, id) => subDoc(b, 'Warehouses', id);

/** A physical count, and the adjustments it produced. */
export const stockTakesCol = (b) => sub(b, 'StockTakes');
export const stockTakeDoc = (b, id) => subDoc(b, 'StockTakes', id);

/**
 * One unified voucher collection with a `type` discriminator, replacing v1's
 * five parallel collections (Sales, Purchases, SalesReturns, PurchaseReturns,
 * Expenses) whose components were copy-pasted and had drifted apart.
 */
export const vouchersCol = (b) => sub(b, 'Vouchers');
export const voucherDoc = (b, id) => subDoc(b, 'Vouchers', id);

/** Immutable append-only stock movements. */
export const stockLedgerCol = (b) => sub(b, 'StockLedger');
export const stockLedgerDoc = (b, id) => subDoc(b, 'StockLedger', id);

/** Immutable append-only party ledger (receivables/payables). */
export const partyLedgerCol = (b) => sub(b, 'PartyLedger');

/* The old `Journals` collection is gone. It was a narrow escape hatch whose
   lines could name only an account, a party or one of twenty-one hardcoded
   nominal heads, and `AccountingVouchers` below does strictly more: any ledger,
   any number of lines, editable, with a revision trail. Nothing was migrated
   because nothing had been posted — see the Tier 0 note in WHATS-MISSING. */

/* ------------------------------------------------------------------ *
 * The general ledger
 *
 * Four collections, and the shape of them is the whole accounting model:
 *
 *   Groups              the Chart of Accounts skeleton — Current Assets, Bank
 *                       Accounts, Indirect Expenses, and any sub-group the
 *                       business creates. Seeded once per business.
 *   Ledgers             one document per named account. A customer, the till,
 *                       "Electricity". Carries its opening balance and a
 *                       denormalised `movement` moved with increment().
 *   AccountingVouchers  one document per financial event, with its lines.
 *   AccountingEntries   one document per LINE. This is the general ledger, and
 *                       every report in the application is derived from it.
 *
 * Why entries are their own collection rather than an array on the voucher:
 * every report reads them ACROSS vouchers — "every posting to Electricity
 * between April and August" is a query on this collection and would otherwise
 * be a scan of every voucher in the year.
 *
 * Entry document ids are DETERMINISTIC: `${voucherId}__${lineIndex}`. That is
 * what makes editing a posted voucher a single transaction — the engine can
 * read, replace and delete exactly the rows a voucher owns without first
 * querying for them, and a transaction cannot run a query. See
 * features/accounting/posting-repository.js.
 * ------------------------------------------------------------------ */

export const groupsCol = (b) => sub(b, 'Groups');
export const groupDoc = (b, id) => subDoc(b, 'Groups', id);

export const ledgersCol = (b) => sub(b, 'Ledgers');
export const ledgerDoc = (b, id) => subDoc(b, 'Ledgers', id);

/**
 * Ledger-name reservations, one document per lowercased name.
 *
 * The same trick `VoucherNumbers` uses for invoice numbers, for the same
 * reason: "is this name free" and "take it" have to be ONE atomic step, and a
 * client-side check of a list that was loaded a minute ago is not one. Two
 * ledgers called "Electricty" and "Electricity" is a reporting bug that takes a
 * year to notice, and the moment to catch it is at creation.
 */
export const ledgerNameDoc = (b, nameKey) => subDoc(b, 'LedgerNames', safeDocId(nameKey));

export const accVouchersCol = (b) => sub(b, 'AccountingVouchers');
export const accVoucherDoc = (b, id) => subDoc(b, 'AccountingVouchers', id);

/** Prior versions of an edited voucher, with the field-level diff. */
export const accRevisionsCol = (b, voucherId) =>
  sub(b, 'AccountingVouchers', voucherId, 'Revisions');
export const accRevisionDoc = (b, voucherId, id) =>
  subDoc(b, 'AccountingVouchers', voucherId, 'Revisions', id);

export const accEntriesCol = (b) => sub(b, 'AccountingEntries');
export const accEntryDoc = (b, id) => subDoc(b, 'AccountingEntries', id);

/** The deterministic id of the entry for one line of one voucher. */
export const accEntryId = (voucherId, lineIndex) => `${voucherId}__${lineIndex}`;

/** Locked accounting periods. See lib/accounting's canPostOn. */
export const periodLocksCol = (b) => sub(b, 'PeriodLocks');
export const periodLockDoc = (b, id) => subDoc(b, 'PeriodLocks', id);

/**
 * Cost centres, branches, projects — the optional second dimension.
 *
 * There is deliberately NO `Bills` collection. Bill-wise outstanding is derived
 * from the `billRefs` on the entries themselves: a bill's outstanding is what
 * its New Reference raised minus what every Against Reference has settled. A
 * second collection holding the same fact is a second collection that can
 * disagree with the ledger, and the ledger is the one that has to be right.
 */
export const costCentresCol = (b) => sub(b, 'CostCentres');
export const costCentreDoc = (b, id) => subDoc(b, 'CostCentres', id);

/* Bank reconciliations: what was tied to what, and when. See lib/reconciliation.js. */
export const reconciliationsCol = (b) => sub(b, 'Reconciliations');
export const reconciliationDoc = (b, id) => subDoc(b, 'Reconciliations', id);

/* Every message the application has sent on the business's behalf, and what
   became of it. See lib/messaging.js. */
export const outboxCol = (b) => sub(b, 'Outbox');
export const outboxDoc = (b, id) => subDoc(b, 'Outbox', id);

/* Payment links raised against documents. See lib/payment-links.js. */
export const paymentLinksCol = (b) => sub(b, 'PaymentLinks');
export const paymentLinkDoc = (b, id) => subDoc(b, 'PaymentLinks', id);

/* What the gateway says actually arrived, written server-side by the webhook. */
export const paymentSettlementsCol = (b) => sub(b, 'PaymentSettlements');
export const paymentSettlementDoc = (b, id) => subDoc(b, 'PaymentSettlements', id);
export const partyLedgerDoc = (b, id) => subDoc(b, 'PartyLedger', id);

export const paymentsCol = (b) => sub(b, 'Payments');
export const paymentDoc = (b, id) => subDoc(b, 'Payments', id);

/**
 * Where the money physically is: the till, each bank account, a card
 * terminal's settlement balance, a wallet, a loan.
 *
 * The party ledger records what SHOULD happen; these record what DID. Without
 * them "how much is in the bank?" has no answer, which is the line between a
 * receivables tracker and an accounting system.
 */
export const accountsCol = (b) => sub(b, 'Accounts');
export const accountDoc = (b, id) => subDoc(b, 'Accounts', id);

/** The cash book. Append-only, like every other ledger in this application. */
export const accountLedgerCol = (b) => sub(b, 'AccountLedger');
export const accountLedgerDoc = (b, id) => subDoc(b, 'AccountLedger', id);

/**
 * Price lists — "wholesale", "retail", "staff".
 *
 * The per-item entries live INSIDE the list document as a map keyed by item id
 * rather than in a subcollection, because pricing a cart means resolving every
 * line at once: a subcollection would be one read per line, on the screen
 * where latency is most visible. The cost is the 1 MiB document limit, which
 * caps a list at roughly ten thousand items — stated here so the next person
 * to hit it knows what the trade was rather than discovering it as a bug.
 */
export const priceListsCol = (b) => sub(b, 'PriceLists');
export const priceListDoc = (b, id) => subDoc(b, 'PriceLists', id);

/**
 * Party groups — "wholesalers", "walk-in", "distributors".
 *
 * A group exists so a price list, a credit period and a discount can be
 * assigned to eighty customers at once instead of to each of them separately
 * and missing four.
 */
export const partyGroupsCol = (b) => sub(b, 'PartyGroups');
export const partyGroupDoc = (b, id) => subDoc(b, 'PartyGroups', id);

/**
 * The activity log: who did what, when.
 *
 * Append-only like the ledgers, and for the same reason — a log a user can
 * edit answers no question worth asking. It is deliberately NOT the ledgers'
 * replacement: the ledgers record what happened to the money, this records who
 * caused it. A business with more than one user cannot otherwise answer "who
 * cancelled that invoice?", which is the question that actually gets asked.
 */
export const activityCol = (b) => sub(b, 'Activity');
export const activityDoc = (b, id) => subDoc(b, 'Activity', id);

/**
 * Bills of materials — what a made thing is made of.
 *
 * One document per recipe, keyed by its own id rather than by the output item,
 * because a business genuinely does have two recipes for the same thing (a
 * cheaper mix for the festival season) and keying by output would make the
 * second one overwrite the first. The output item id is a FIELD, and the
 * active recipe for an item is the one flagged `isDefault`.
 */
export const bomsCol = (b) => sub(b, 'Boms');
export const bomDoc = (b, id) => subDoc(b, 'Boms', id);

/**
 * Production runs. A run is a document for the same reason a voucher is: it
 * consumed real stock and created real stock, and both movements have to be
 * traceable to the thing that caused them.
 */
export const productionsCol = (b) => sub(b, 'Productions');
export const productionDoc = (b, id) => subDoc(b, 'Productions', id);

/**
 * Repeating documents — rent, an AMC, a monthly supply contract.
 *
 * The schedule holds a TEMPLATE of the document rather than a reference to a
 * past one. A reference would mean a schedule that silently changes when
 * somebody edits the invoice it points at, and one that breaks when that
 * invoice is cancelled.
 */
export const recurringCol = (b) => sub(b, 'Recurring');
export const recurringDoc = (b, id) => subDoc(b, 'Recurring', id);

/**
 * Turn an arbitrary string into a legal Firestore document id.
 *
 * WHY THIS EXISTS
 * Invoice numbers are formatted like `INV/2026-27/0001` — the slash is the
 * conventional separator on an Indian tax invoice. Passing that straight into
 * `doc()` does NOT create a document with a slash in its id: Firestore treats
 * every `/` as a path separator, so
 *
 *     doc(db, 'Businesses', bid, 'VoucherNumbers', 'sales_2026-2027_INV/2026-27/0001')
 *
 * silently resolves to the four-segment path
 *
 *     Businesses/{bid}/VoucherNumbers/sales_2026-2027_INV/2026-27/0001
 *
 * i.e. a document nested two collections deeper than intended. The security
 * rules match `/VoucherNumbers/{numberId}` — a single segment — so the deeper
 * path fell through to the catch-all deny and every invoice save failed with
 * "Missing or insufficient permissions".
 *
 * The encoding must be INJECTIVE. Replacing `/` with `-` would map both
 * `INV/2026-27/0001` and `INV-2026-27-0001` onto the same id, and the second
 * one would then be rejected as a duplicate number that the user never used.
 * `encodeURIComponent` is a bijection onto a slash-free alphabet, so distinct
 * numbers always produce distinct ids.
 *
 * Firestore additionally forbids ids that are `.` or `..`, or that match
 * `__.*__`, and caps them at 1500 bytes.
 */
export function safeDocId(value) {
  const raw = String(value ?? '').trim();
  if (!raw) throw new Error('Cannot build a document id from an empty value.');

  // Removes '/' along with anything else that needs escaping. ASCII output,
  // so string length equals byte length from here on.
  let id = encodeURIComponent(raw);

  // '.' and '..' are reserved; encode the dots rather than appending, so the
  // mapping stays injective.
  if (/^\.{1,2}$/.test(id)) id = id.replace(/\./g, '%2E');

  // '__foo__' is reserved for internal use. Encoding the leading underscores
  // keeps this distinct from any other input.
  if (/^__.*__$/.test(id)) id = `%5F%5F${id.slice(2)}`;

  if (id.length > 1500) {
    throw new Error('That voucher number is too long to store.');
  }
  return id;
}

/**
 * Reserved voucher numbers, used to enforce uniqueness.
 * The document id IS the number, so creating it inside the save transaction
 * makes "is this number free" and "take it" a single atomic step.
 */
export const numberReservationDoc = (b, type, fyKey, number) =>
  subDoc(b, 'VoucherNumbers', safeDocId(`${type}_${fyKey}_${number}`));

/* Storage paths ----------------------------------------------------- */

export const storagePaths = {
  businessLogo: (b) => `Businesses/${requireBusiness(b)}/branding/logo`,
  businessSignature: (b) => `Businesses/${requireBusiness(b)}/branding/signature`,
  itemImage: (b, itemId) => `Businesses/${requireBusiness(b)}/items/${itemId}`,
  partyImage: (b, partyId) => `Businesses/${requireBusiness(b)}/parties/${partyId}`,
};

/** Counter key for a voucher series, scoped to the financial year. */
export const counterKey = (voucherType, fyKey) => `${voucherType}_${fyKey}`;
